import { CubeGlobal } from "./HCViewerUtils";

export const getAggregateMeasurementBoundingBox = ({ measurements }: any) => {
  let bounding;

  measurements.forEach((measurement: any, index: number) => {
    if (!bounding) {
      bounding = measurements[index]?.getBoundingBox();
    }
    measurement && measurement.select();
    const box = measurement?.getBoundingBox();
    box && bounding && bounding.addBox(box);
  });
  return bounding;
};

const signedArea = function (loop: any) {
  let area = 0;
  const n = loop.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += loop[i][0] * loop[j][1] - loop[j][0] * loop[i][1];
  }

  return area / 2;
};

const classifyLoops = function (loops: any) {
  if (loops.length === 0) return { outerLoops: [], innerLoops: [] };

  // Compute areas and track largest
  let largestLoop = null;
  let largestArea = -Infinity;
  const loopAreas = loops.map((loopObj: any) => {
    const area = signedArea(loopObj);
    return { loop: loopObj, area: Math.abs(area) }; // Use absolute area
  });

  // Find the largest loop
  loopAreas.forEach((loopData: any) => {
    if (loopData.area > largestArea) {
      largestArea = loopData.area;
      largestLoop = loopData.loop;
    }
  });

  // Classify loops
  const outerLoops = [largestLoop];
  const innerLoops = loopAreas.filter((l: any) => l.loop !== largestLoop).map((l: any) => l.loop);

  return { outerLoops, innerLoops };
};

// Tolerance for floating-point comparison
const TOLERANCE = 1e-6;

// Function to compare two vertices with tolerance
export const compareVertices = function (v1: any, v2: any, tolerance = TOLERANCE) {
  return (
    Math.abs(v1[0] - v2[0]) < tolerance && Math.abs(v1[1] - v2[1]) < tolerance && Math.abs(v1[2] - v2[2]) < tolerance
  );
};

export const findBoundaryLoops = function findBoundaryLoops(vertices: any) {
  // Step 1: Extract all edges
  const edges: any[] = [];
  for (let i = 0; i < vertices.length; i += 3) {
    const v1 = vertices[i];
    const v2 = vertices[i + 1];
    const v3 = vertices[i + 2];
    edges.push([v1, v2]);
    edges.push([v2, v3]);
    edges.push([v3, v1]);
  }

  // Step 2: Normalize edge representation (sort vertices in each edge)
  const normalizedEdges = edges.map((edge: any) =>
    edge.sort((a: any, b: any) => a.toString().localeCompare(b.toString())),
  );

  // Step 3: Count edge occurrences
  const edgeCount = new Map();
  for (const edge of normalizedEdges) {
    const edgeKey = JSON.stringify(edge);
    edgeCount.set(edgeKey, (edgeCount.get(edgeKey) || 0) + 1);
  }

  // Step 4: Identify boundary edges (edges that appear only once)
  const boundaryEdges: any[] = [];
  for (const [edgeKey, count] of edgeCount.entries()) {
    if (count === 1) {
      boundaryEdges.push(JSON.parse(edgeKey));
    } else if (count > 2) {
      // Non-manifold edge detected (edge shared by more than 2 triangles)
      console.warn("Non-manifold edge detected. Returning empty result.");
      return { outerLoops: [], innerLoops: [] };
    }
  }

  // If no boundary edges, the mesh is closed
  if (boundaryEdges.length === 0) {
    console.warn("No boundary edges found. The mesh is fully closed.");
    return { outerLoops: [], innerLoops: [] };
  }

  // Step 5: Group boundary edges into loops
  const loops: any[] = [];
  while (boundaryEdges.length > 0) {
    const startEdge = boundaryEdges.shift();
    if (!startEdge) break;
    const loop: any[] = [startEdge[0], startEdge[1]];
    let currentVertex = loop[loop.length - 1];
    while (true) {
      let nextEdge = null;
      let nextEdgeIndex = -1;

      // Find the next edge connected to the current vertex
      for (let i = 0; i < boundaryEdges.length; i++) {
        const edge = boundaryEdges[i];
        if (edge && (compareVertices(edge[0], currentVertex) || compareVertices(edge[1], currentVertex))) {
          nextEdge = edge;
          nextEdgeIndex = i;
          break;
        }
      }

      if (!nextEdge) {
        // Non-manifold vertex detected (boundary edge not connected properly)
        console.warn("Non-manifold vertex detected. Returning empty result.");
        return { outerLoops: [], innerLoops: [] };
      }

      // Remove the next edge from the boundary edges list
      boundaryEdges.splice(nextEdgeIndex, 1);

      // Add the next vertex to the loop
      const nextVertex = compareVertices(nextEdge[0], currentVertex) ? nextEdge[1] : nextEdge[0];
      loop.push(nextVertex);
      currentVertex = nextVertex;

      // Check if the loop is closed
      if (compareVertices(currentVertex, loop[0])) {
        loops.push(loop);
        break;
      }
    }
  }
  const classifiledLoops = classifyLoops(loops);
  return classifiledLoops;
};

export const fillMeasurementPointsAndFinalize = function (markup: any, loop: any) {
  for (const v of loop) {
    markup.addPoint(v);
  }
  markup.leaderPosition = loop[0];
  markup.textPosition = markup.leaderPosition;
  if (markup.setCalibrationScale) markup.setCalibrationScale(CubeGlobal.calibrationScale);

  markup.finalize();
  viewerImpl.hwv.measureManager.addMeasurement(markup);
  viewerImpl.hwv.measureManager.finalizeMeasurement(markup);
};

export const createMeasuremntMarkup = function (operatorId: any, loops: any) {
  let markup: any = null;
  const loopsArray = [...loops.innerLoops, ...loops.outerLoops];

  switch (operatorId) {
    case CubeGlobal.OperatorId.QTakeOffAreaOperator:
    case CubeGlobal.OperatorId.QTakeOffFreeHandAreaOperator:
      for (const loop of loopsArray) {
        markup = new CubeGlobal.QTakeOffAreaMarkup(viewerImpl.hwv);
        if (markup) {
          markup.setName("QTakeOffAreaMarkup");
          fillMeasurementPointsAndFinalize(markup, loop);
        }
      }
      break;
    case CubeGlobal.OperatorId.QTakeOffPerimeterOperator:
      for (const loop of loopsArray) {
        markup = new CubeGlobal.QTakeOffLengthMarkup(viewerImpl.hwv);
        if (markup) {
          markup.setName("QTakeOffLengthMarkup");
          fillMeasurementPointsAndFinalize(markup, loop);
        }
      }
      break;
    default:
      break;
  }
  return markup;
};
